<script setup>
import {
    testLinkScheme,
    testInternalLink,
    testRelativeLink,
    formatUrl,
    checkActive,
    testValidDomains,
} from '@/composables/helpers/links';

const props = defineProps({
    href: {
        type: String,
        default: '',
        required: false,
    },
    replace: {
        type: Boolean,
        default: false,
    },
    target: {
        type: [String, Boolean],
        default: false,
    },
    windowed: {
        type: Boolean,
        default: false,
    },
    forceInternalLink: {
        type: Boolean,
        default: false,
    },
    forceExternalLink: {
        type: Boolean,
        default: false,
    },
});

// Variables
const route = useRoute()

const internal = computed(() => {
    return props.href ? testRelativeLink(props.href) : false
})

const relative = computed(() => {
    return props.href ? testInternalLink(props.href) : false
})

const validDomain = computed(() => {
    return props.href ? testValidDomains(props.href) : false
})

const validScheme = computed(() => {
    return props.href ? testLinkScheme(props.href) : false
})

const isIframe = computed(() => {
    return internal.value && route.path.startsWith('/iframe') && !route.path.includes('/iframe/search')
})

const iFrameUrl = computed(() => {
    return props.href.startsWith('/iframe') ? props.href : `/iframe${props.href}`
})

const active = computed(() => {
    return props.href ? checkActive(props.href, route.path) : false
})

const url = computed(() => {
    return internal.value ? formatUrl(props.href, relative.value) : props.href
})

const replaceState = () => {
    history.replaceState({}, null, url.value)
}

const openWindow = () => {
    window.open(url.value, '_blank', 'width=500,height=500')
}

const setActiveClass = () => {
    if (active.value == 1) return 'mess-active-link';
    if (active.value == 2) return 'mess-active-link';
    return '';
}

const forceRefresh = computed(() => {
    // If the link is on the current page, and has parameters we should replace the state
    let hit = false;
    const onCurrentPage = props.href.includes(route.path);
    // If we are on the current page, and the link has a query, we should force a refresh
    // EG: route.path = /agenda, url.value = /agenda?day=1 -> force refresh
    // EG: route.path = /agenda, url.value = /agenda -> no refresh
    if (onCurrentPage) hit = props.href.includes('?');
    // If we are on the current page, have params, and the link is the root page, we should force a refresh
    // EG: route.path = /agenda, url.value = / -> no refresh
    // EG: route.path = /agenda?day=1, url.value = /agenda -> force refresh
    if (onCurrentPage && !hit) {
        const hasParams = route.path.includes('?')
        const isRootPage = route.path === props.href.value;
        hit = hasParams && isRootPage;
    }
    return false;
});

</script>

<template>
    <a v-if="replace" :href="url" class="mess-link replace" @click.prevent="replaceState()">
        <slot />
    </a>
    <a v-else-if="isIframe" :href="iFrameUrl">
        <slot />
    </a>
    <a v-else-if="forceRefresh && internal" :href="url"
        :class="`mess-link force-refresh internal-link ${setActiveClass()}`">
        <slot />
    </a>
    <a v-else-if="windowed" class="mess-link window" :href="url" @click.prevent="openWindow()">
        <slot />
    </a>
    <NuxtLink :to="url" v-else-if="internal || forceInternalLink"
        :class="`mess-link internal-link ${setActiveClass()}`">
        <slot />
    </NuxtLink>
    <span v-else-if="!validScheme && !validDomain">
        <slot />
    </span>
    <a :href="'https://' + url" :target="target ? target : '_blank'" v-else-if="!validScheme"
        class="mess-link external-link">
        <slot />
    </a>
    <a :href="url" :target="target ? target : '_blank'" v-else class="mess-link external-link">
        <slot />
    </a>
</template>